import { RING_COUNT, ringRadius } from "./particle-colours";

/*
 * The flights of the hero's egress: particles that leave the settled rings
 * and land on places in the product. Plain geometry in pixels and
 * milliseconds, apart from the canvas that draws it (evidence-egress), so
 * the paths can be checked without a page.
 */

/** The glass the particles leave: its centre and radius. */
export type EgressGlass = { x: number; y: number; radius: number };

/** A place particles land on: its box, how many land there, and how long after the egress starts its first one leaves. */
export type EgressTarget = { x: number; y: number; width: number; height: number; count: number; leadMs: number };

/** One particle's flight: from its ring, round a bend, to its place on the target. */
export type EgressFlight = {
  ring: number;
  startX: number;
  startY: number;
  bendX: number;
  bendY: number;
  endX: number;
  endY: number;
  delayMs: number;
  durationMs: number;
  /** The particle's radius in flight, in pixels. */
  size: number;
};

/** How long after a target's lead its particles keep leaving. */
const LEAVING_MS = 200;
/** The shortest and the longest flight. */
const FLIGHT_MS = { from: 460, to: 720 } as const;
/** The furthest a path bows from the straight line, as a share of its length and in pixels. */
const BOW = { share: 0.3, most: 90 } as const;
const SIZE = { from: 0.8, to: 1.6 } as const;

const ease = (value: number) => {
  const clamped = Math.min(Math.max(value, 0), 1);
  return clamped * clamped * (3 - 2 * clamped);
};

function seeded(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

/** A ring picked by its share of the rings' area, so the outer rings give most. */
function pickRing(random: number): number {
  const total = RING_COUNT * RING_COUNT;
  for (let ring = 0; ring < RING_COUNT; ring += 1) {
    if (random * total < (ring + 1) * (ring + 1)) return ring;
  }
  return RING_COUNT - 1;
}

/**
 * Every flight, the same on every run: each target's particles leave from
 * points spread over the rings and land spread across the target's box.
 */
export function buildEgressFlights(glass: EgressGlass, targets: readonly EgressTarget[], seed = 20261001): EgressFlight[] {
  const random = seeded(seed);
  const flights: EgressFlight[] = [];
  for (const target of targets) {
    for (let index = 0; index < target.count; index += 1) {
      const ring = pickRing(random());
      const { middle, halfWidth } = ringRadius(ring);
      const radius = (middle + (random() * 2 - 1) * halfWidth) * glass.radius;
      const angle = random() * Math.PI * 2;
      const startX = glass.x + Math.cos(angle) * radius;
      const startY = glass.y + Math.sin(angle) * radius;
      const endX = target.x + random() * target.width;
      const endY = target.y + (0.2 + random() * 0.6) * target.height;
      const length = Math.max(Math.hypot(endX - startX, endY - startY), 1);
      const bow = (random() * 2 - 1) * Math.min(length * BOW.share, BOW.most);
      flights.push({
        ring,
        startX,
        startY,
        bendX: (startX + endX) / 2 + (-(endY - startY) / length) * bow,
        bendY: (startY + endY) / 2 + ((endX - startX) / length) * bow,
        endX,
        endY,
        delayMs: target.leadMs + random() * LEAVING_MS,
        durationMs: FLIGHT_MS.from + random() * (FLIGHT_MS.to - FLIGHT_MS.from),
        size: SIZE.from + random() * (SIZE.to - SIZE.from),
      });
    }
  }
  return flights;
}

/**
 * Where a particle is `atMs` after the egress starts, and its radius there:
 * it swells as it leaves its ring and shrinks into its place. Nothing
 * before it leaves or once it has landed.
 */
export function egressPoint(flight: EgressFlight, atMs: number): { x: number; y: number; radius: number } | null {
  const progress = (atMs - flight.delayMs) / flight.durationMs;
  if (progress <= 0 || progress >= 1) return null;
  const along = ease(progress);
  const rest = 1 - along;
  const presence = ease(progress / 0.12) * (1 - ease((progress - 0.8) / 0.2));
  return {
    x: rest * rest * flight.startX + 2 * rest * along * flight.bendX + along * along * flight.endX,
    y: rest * rest * flight.startY + 2 * rest * along * flight.bendY + along * along * flight.endY,
    radius: flight.size * Math.sqrt(presence),
  };
}

/** When most of a target's particles have landed: the moment the target answers them. */
export function egressLandsAt(target: Pick<EgressTarget, "leadMs">): number {
  return target.leadMs + LEAVING_MS / 2 + (FLIGHT_MS.from + FLIGHT_MS.to) / 2;
}

/** When the last particle of any target has landed. */
export function egressEndsAt(targets: readonly Pick<EgressTarget, "leadMs">[]): number {
  return Math.max(0, ...targets.map((target) => target.leadMs)) + LEAVING_MS + FLIGHT_MS.to;
}
